// @flow
import React from "react"
import classnames from "classnames"
import bind from "memoize-bind"
import RouteNoStops from "@kiwicom/orbit-components/lib/icons/RouteNoStops"

import TripPlace from "common/components/Trip/TripPlace"
import { resolveTripPlaceData } from "common/services/starred/utils"
import JourneyActionButtons from "common/components/JourneyActionButtons/JourneyActionButtons"
import Timestamp from "common/components/Timestamp/Timestamp"
import { itemIsValid, itemPriceChanged } from "common/records/Starred"
import type { StarredItem, StarredPlaces } from "common/records/Starred.types"
import type Journey from "common/records/Journey"
import type PassengersCount from "common/records/PassengersCount"
import Price from "common/components/Price/Price"
import { getRedirectionType } from "mmb/scenes/Starred/components/StarredMonthItem/StarredMonthItem"
import Translate from "../../../../components/Translate"

const PASSENGERS_COUNT = {
  infants: __("result.infants_count"),
  adults: __("result.adults_count"),
}

type OnRemove = (StarredItem, SyntheticEvent<*>) => void

type Props = {
  item: StarredItem,
  starredPlaces: StarredPlaces,
  onRemove: OnRemove,
  onToggle: (Journey, PassengersCount) => void,
  goToJourney: (SyntheticEvent<*>) => void,
  shareUrl: string,
}

const getPriceUpdateInfo = (item: StarredItem, onRemove: OnRemove) => {
  if (!itemIsValid(item)) {
    return (
      <span>
        <Translate tKey="starred.flight_not_available" />{" "}
        <a onClick={bind(onRemove, null, item)}>
          <Translate tKey="starred.remove_starred" />
        </a>
      </span>
    )
  }

  const lastUpdate = <Timestamp value={item.priceUpdatedAt} relative />

  if (itemPriceChanged(item)) {
    return (
      <Translate
        tKey="starred.price_update_changed"
        values={{ lastUpdate, origPrice: <Price>{item.lastPrice}</Price> }}
        dynamic
      />
    )
  }

  return item.priceUpdatedAt ? (
    <Translate tKey="starred.price_update" values={{ lastUpdate }} dynamic />
  ) : (
    <Translate
      tKey="starred.created_at"
      values={{ createdAt: <Timestamp value={item.createdAt} relative /> }}
      dynamic
    />
  )
}

const NavbarStarredItem = ({
  item,
  starredPlaces,
  goToJourney,
  onRemove,
  onToggle,
  shareUrl,
}: Props) => {
  const { journey } = item

  const passengersCount = item.form.passengers.getSum()

  return (
    journey && (
      <div
        className={classnames("NavbarStarredItem", { _invalid: !itemIsValid(item) })}
        onClick={goToJourney}
      >
        <div className="NavbarStarredItem-priceUpdate-and-redirectionType-row">
          <span className="NavbarStarredItem-priceUpdate">
            {getPriceUpdateInfo(item, onRemove)}
          </span>
          <span className="NavbarStarredItem-redirectionType">{getRedirectionType(item)}</span>
        </div>
        <div className="NavbarStarredItem-data">
          <div className="NavbarStarredItem-info">
            <div className="NavbarStarredItem-price">
              <span className="price indent-left">
                <Price maxSize={99} showCurrencySymbol>
                  {journey.getBestPrice()}
                </Price>
              </span>

              {passengersCount > 1 && (
                <span className="NavbarStarredItem-passengers spTypo-normal">
                  {item.form.passengers.isMulti() ? (
                    <Translate tKey="result.total_price" />
                  ) : (
                    <Translate
                      tKey={PASSENGERS_COUNT[item.form.passengers.getTransKey()]}
                      values={{ count: passengersCount }}
                    />
                  )}
                </span>
              )}
            </div>
            <div className="NavbarStarredItem-actionButtons">
              <JourneyActionButtons
                starEnabled
                starActive
                onStarToggle={onToggle}
                shareEnabled
                shareDetached
                fixed
                shareUrl={shareUrl}
                journey={item.journey}
                passengers={item.form.passengers}
                cabinClass={item.form.cabinClass}
              />
            </div>
          </div>
          <div className="NavbarStarredItem-trips">
            {journey.trips.map(trip => {
              const departure = trip.getDeparture()
              const departurePlace = resolveTripPlaceData(departure.get("where"), starredPlaces)
              const arrival = trip.getArrival()
              const arrivalPlace = resolveTripPlaceData(arrival.get("where"), starredPlaces)

              return (
                <div className="NavbarStarredItem-trip" key={trip.getId()}>
                  <div className="NavbarStarredItem-trip-field _left">
                    <div className="NavbarStarredItem-trip-longText">
                      <div className="NavbarStarredItem-trip-airport-codes">
                        <TripPlace className="from" place={departurePlace} />
                        <span className="flight-arrow">
                          <RouteNoStops />
                        </span>
                        <TripPlace className="to" place={arrivalPlace} />
                      </div>
                      <div className="NavbarStarredItem-trip-date">
                        <Timestamp
                          // eslint-disable-next-line no-template-curly-in-string
                          format="${dateFormat}"
                          value={departure.getIn(["when", "local"])}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="NavbarStarredItem-trip-field _right">
                    <div className="NavbarStarredItem-trip-flight-duration">
                      <Timestamp
                        value={trip.getDuration()}
                        // eslint-disable-next-line no-template-curly-in-string
                        format="${durationFormat}"
                      />
                    </div>
                    <div className="NavbarStarredItem-trip-time">
                      <span className="NavbarStarredItem-trip-time-items">
                        <Timestamp
                          value={departure.getIn(["when", "local"])}
                          // eslint-disable-next-line no-template-curly-in-string
                          format="${timeFormat}"
                        />
                        <span className="_dash"> - </span>
                        <Timestamp
                          value={arrival.getIn(["when", "local"])}
                          // eslint-disable-next-line no-template-curly-in-string
                          format="${timeFormat}"
                        />
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  )
}

export default NavbarStarredItem
